import type { InsertEnterpriseRiskMutation } from '@risksmart-app/web-graphql-client/generated/graphql';
import {
  InsertEnterpriseRiskDocument,
  namedOperations,
} from '@risksmart-app/web-graphql-client/generated/graphql';

import { createMutationHook } from '@/utils/hook-factories';

import type { EnterpriseRiskMutationVariables } from '../nativeMutationInputs';
import type { FrontendRouterInputs } from '../routerInputs';

type InsertEnterpriseRiskTRPCInput =
  FrontendRouterInputs['enterpriseRisk']['insert'];

export const useInsertEnterpriseRisk = createMutationHook({
  name: 'insertEnterpriseRisk',
  mutationOptions: (trpc, onSuccess) =>
    trpc.frontend.enterpriseRisk.insert.mutationOptions({ onSuccess }),
  mapInput: (
    variables: EnterpriseRiskMutationVariables
  ): InsertEnterpriseRiskTRPCInput => {
    const base = {
      Title: variables.Title,
      Description: variables.Description ?? '',
      Treatment: variables.Treatment ?? null,
      CustomAttributeData: variables.CustomAttributeData ?? null,
    };

    if (variables.Tier === 1) {
      return { ...base, Tier: 1, ParentId: null };
    }

    if (!variables.ParentId) {
      throw new Error(
        'A parent enterprise risk is required for tier 2 and tier 3 risks'
      );
    }

    if (variables.Tier === 2) {
      return { ...base, Tier: 2, ParentId: variables.ParentId };
    }

    if (variables.Tier === 3) {
      return { ...base, Tier: 3, ParentId: variables.ParentId };
    }

    throw new Error(`Unsupported enterprise risk tier: ${variables.Tier}`);
  },
  invalidate: (trpc) => [
    trpc.frontend.enterpriseRisk.register.queryKey(),
    trpc.frontend.enterpriseRisk.getById.queryKey(),
    trpc.frontend.enterpriseRisk.getByTier.queryKey(),
  ],
  // Maps the tRPC create response (the full enterprise_risk row, which carries
  // the generated Id) to the GraphQL mutation shape.
  mapResponse: (result: { Id: string }): InsertEnterpriseRiskMutation => ({
    insertChildEnterpriseRisk: {
      __typename: 'IdOutput',
      Id: result.Id,
    },
  }),
  graphqlDocument: InsertEnterpriseRiskDocument,
  graphqlVariables: (variables: EnterpriseRiskMutationVariables) => ({
    ...variables,
  }),
  evictFields: ['enterprise_risk'],
  refetchQueries: [
    namedOperations.Query.getEnterpriseRiskById,
    namedOperations.Query.getEnterpriseRisksFlat,
    namedOperations.Query.getEnterpriseRisksByTier,
    namedOperations.Query.getEnterpriseRisks,
  ],
});
